import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Nav, Tab, Badge, Button, Spinner, Alert, Form, Table, ButtonGroup } from 'react-bootstrap';
import { 
  FaArrowLeft, FaEnvelope, FaFilePdf, FaChartLine, 
  FaDollarSign, FaClock, FaExclamationTriangle, FaMapMarkedAlt,
  FaCamera, FaHistory, FaCheck, FaTimes, FaEye
} from 'react-icons/fa';
import dealService, { DealDetail, MLSData } from '../../services/dealService';
import DealMap from './DealMap';
import DealFinancials from './DealFinancials';
import DealComps from './DealComps';
import DealMLSDetails from './DealMLSDetails';
import SendToClientModal from './SendToClientModal';
import './DealAnalysis.css';

interface DealAnalysisProps {
  // Optional override for the deal id — when provided, used instead of
  // the URL param. Lets the Agent Portal Docs Hub render DealAnalysis
  // inline as a hub tab body without depending on the /investor-deals/:id
  // route (which would unmount the HubShell chrome).
  dealId?: number;
  // Optional back handler — when provided, the internal "Back to Deals"
  // button calls this instead of navigate('/investor-deals'). Lets the
  // hub return to its tab deal list without a full route change.
  onBack?: () => void;
}

const DealAnalysis: React.FC<DealAnalysisProps> = ({ dealId: propDealId, onBack }) => {
  const { id: urlId } = useParams<{ id: string }>();
  const id = propDealId != null ? String(propDealId) : urlId;
  const navigate = useNavigate();

  // Unified back behavior — parent override wins, route fallback otherwise.
  const handleBack = () => {
    if (onBack) onBack();
    else navigate('/investor-deals');
  };
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [mlsData, setMlsData] = useState<MLSData | null>(null);
  const [compDetails, setCompDetails] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Status management
  const [dealStatus, setDealStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  
  // Send to Client modal
  const [showSendModal, setShowSendModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDealData(parseInt(id));
    }
  }, [id]);

  const fetchDealData = async (dealId: number) => {
    try {
      setLoading(true);
      
      // Fetch deal details
      const dealResponse = await dealService.getDealDetail(dealId);
      if (dealResponse.success) {
        setDeal(dealResponse.data.deal);
        setCompDetails(dealResponse.data.comp_details || []);
        setDealStatus(dealResponse.data.deal.deal_status || 'new');
        setAssignedTo(dealResponse.data.deal.assigned_to || '');
        setNotes(dealResponse.data.deal.notes || '');
      }
      
      // Fetch MLS data
      try {
        const mlsResponse = await dealService.getDealMLSData(dealId);
        if (mlsResponse.success) {
          setMlsData(mlsResponse.data);
        }
      } catch (mlsError) {
        console.error('Failed to fetch MLS data:', mlsError);
        // Don't fail the whole page if MLS data isn't available
      }
      
    } catch (err: any) {
      console.error('Error fetching deal:', err);
      setError(err.message || 'Failed to fetch deal details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!deal) return;
    
    try {
      setStatusUpdating(true);
      const response = await dealService.updateDealStatus(
        deal.id, 
        dealStatus,
        assignedTo || undefined,
        notes || undefined
      );
      
      if (response.success) {
        // Update local state
        setDeal({
          ...deal,
          deal_status: dealStatus,
          assigned_to: assignedTo,
          notes: notes
        });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update deal status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleQuickStatusUpdate = async (newStatus: string) => {
    if (!deal) return;
    
    try {
      setStatusUpdating(true);
      const response = await dealService.updateDealStatus(deal.id, newStatus);
      
      if (response.success) {
        // Update local state
        setDealStatus(newStatus);
        setDeal({
          ...deal,
          deal_status: newStatus
        });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update deal status');
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading deal analysis...</p>
      </Container>
    );
  }

  if (error || !deal) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || 'Deal not found'}
        </Alert>
        <Button variant="primary" onClick={handleBack}>
          <FaArrowLeft className="me-2" />
          Back to Deals
        </Button>
      </Container>
    );
  }

  const getGradeStyles = () => {
    const color = dealService.getGradeColor(deal.flip_grade);
    return {
      backgroundColor: color,
      color: 'white'
    };
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      new: 'primary',
      reviewing: 'info',
      pursuing: 'warning',
      offered: 'secondary',
      won: 'success',
      passed: 'danger',
      expired: 'dark'
    };
    
    const labels: Record<string, string> = {
      new: '🆕 New',
      reviewing: '📋 Reviewing',
      pursuing: '🎯 Pursuing',
      offered: '💼 Offered',
      won: '✅ Won',
      passed: '❌ Passed',
      expired: '⏰ Expired'
    };

    return (
      <Badge bg={variants[status] || 'secondary'} className="fs-6">
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <Button
                variant="link"
                onClick={handleBack}
                className="me-3 text-decoration-none"
              >
                <FaArrowLeft className="me-2" />
                Back to Deals
              </Button>
              <h3 className="mb-0">
                {deal.unparsed_address || deal.address || `${deal.city}, AZ ${deal.postal_code}`}
              </h3>
            </div>
            <div className="d-flex align-items-center">
              {/* Quick Status Action Buttons */}
              <div className="me-3">
                <small className="text-muted me-2">Quick Actions:</small>
                <ButtonGroup size="sm">
                  {dealStatus === 'new' && (
                    <Button
                      variant="success"
                      onClick={() => handleQuickStatusUpdate('reviewing')}
                      disabled={statusUpdating}
                      title="Mark as Reviewing"
                    >
                      <FaCheck className="me-1" /> Start Review
                    </Button>
                  )}
                  {(dealStatus === 'new' || dealStatus === 'reviewing') && (
                    <Button
                      variant="danger" 
                      onClick={() => handleQuickStatusUpdate('passed')}
                      disabled={statusUpdating}
                      title="Pass on Deal"
                    >
                      <FaTimes className="me-1" /> Pass
                    </Button>
                  )}
                  {dealStatus === 'reviewing' && (
                    <Button
                      variant="warning"
                      onClick={() => handleQuickStatusUpdate('pursuing')}
                      disabled={statusUpdating}
                      title="Start Pursuing"
                    >
                      <FaEye className="me-1" /> Pursue
                    </Button>
                  )}
                </ButtonGroup>
              </div>
              
              {/* Existing Action Buttons */}
              <Button 
                variant="outline-primary" 
                className="me-2"
                onClick={() => setShowSendModal(true)}
              >
                <FaEnvelope className="me-2" />
                Send to Client
              </Button>
              <Button variant="outline-secondary">
                <FaFilePdf className="me-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mb-4 g-3">
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <div className="d-flex justify-content-center align-items-center mb-2">
                <Badge style={getGradeStyles()} className="px-3 py-2">
                  <h5 className="mb-0">Grade: {deal.flip_grade}</h5>
                </Badge>
                <span className="ms-2" style={{ fontSize: '1.5em' }}>
                  {dealService.getGradeEmoji(deal.flip_grade)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaChartLine className="text-success mb-2" size={24} />
              <h6 className="text-muted small mb-1">ROI</h6>
              <h4 className="mb-0 text-success">{dealService.formatROI(deal.flip_roi)}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaDollarSign className="text-primary mb-2" size={24} />
              <h6 className="text-muted small mb-1">Profit</h6>
              <h4 className="mb-0 text-primary">{dealService.formatCurrency(deal.flip_profit)}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaClock className="text-warning mb-2" size={24} />
              <h6 className="text-muted small mb-1">Days on Market</h6>
              <h4 className="mb-0">{mlsData?.days_on_market || deal.days_on_market || 0}</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={6} md={2}>
          <Card className="text-center h-100">
            <Card.Body>
              <FaExclamationTriangle className="text-danger mb-2" size={24} />
              <h6 className="text-muted small mb-1">Distress Score</h6>
              <h4 className="mb-0">{deal.distress_score}/5</h4>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xs={12} md={2}>
          <Card className="h-100">
            <Card.Body className="d-flex flex-column">
              {/* Status Badge - Prominent Display */}
              <div className="text-center mb-3">
                <h6 className="text-muted small mb-2">Current Status</h6>
                {getStatusBadge(dealStatus)}
              </div>
              
              {/* Status Dropdown for Changes */}
              <Form.Group className="mb-2 flex-grow-1">
                <Form.Label className="small text-muted">Change Status</Form.Label>
                <Form.Select 
                  size="sm" 
                  value={dealStatus} 
                  onChange={(e) => setDealStatus(e.target.value)}
                >
                  <option value="new">New</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="pursuing">Pursuing</option>
                  <option value="offered">Offered</option>
                  <option value="won">Won</option>
                  <option value="passed">Passed</option>
                </Form.Select>
              </Form.Group>
              
              {/* Update Button */}
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handleStatusUpdate}
                disabled={statusUpdating}
                className="mb-2"
              >
                {statusUpdating ? 'Updating...' : 'Update Status'}
              </Button>
              
              <Form.Group>
                <Form.Label className="small text-muted">Assigned To</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Unassigned"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabbed Content */}
      <Card className="deal-analysis-tabs">
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'overview')}>
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link eventKey="overview">Overview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="comps">
                  Comps Analysis
                  {deal.total_comps && (
                    <Badge bg="secondary" className="ms-2">{deal.total_comps}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="financials">Financials</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="map">
                  <FaMapMarkedAlt className="me-1" />
                  Map View
                </Nav.Link>
              </Nav.Item>
              {mlsData?.photos && mlsData.photos.length > 0 && (
                <Nav.Item>
                  <Nav.Link eventKey="photos">
                    <FaCamera className="me-1" />
                    Photos
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="activity">
                  <FaHistory className="me-1" />
                  Activity
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                {/* MLS Details Component */}
                <DealMLSDetails 
                  deal={deal} 
                  mlsImages={mlsData?.images || mlsData?.photos}
                  mlsData={mlsData}
                />
                
                {/* Flip Analysis Summary */}
                <Row className="mt-4">
                  <Col md={6}>
                    <Card>
                      <Card.Header className="bg-secondary text-white">
                        <h5 className="mb-0">
                          <FaDollarSign className="me-2" />
                          Flip Analysis Summary
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <Table className="table-sm mb-0">
                          <tbody>
                            <tr>
                              <td className="text-muted">Purchase Price:</td>
                              <td className="fw-bold">{dealService.formatCurrency(deal.list_price)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">Renovation Est:</td>
                              <td>{dealService.formatCurrency(deal.reno_estimate)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">Holding (4mo):</td>
                              <td>{dealService.formatCurrency(deal.holding_costs || 0)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted">Selling (6%):</td>
                              <td>{dealService.formatCurrency(deal.selling_costs || 0)}</td>
                            </tr>
                            <tr className="border-top">
                              <td className="text-muted fw-bold">Total Investment:</td>
                              <td className="fw-bold">{dealService.formatCurrency(deal.total_all_in || 0)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted fw-bold">After Repair Value:</td>
                              <td className="fw-bold text-success">{dealService.formatCurrency(deal.renovated_arv)}</td>
                            </tr>
                            <tr className="border-top">
                              <td className="text-muted fw-bold">Net Profit:</td>
                              <td className="fw-bold text-success">{dealService.formatCurrency(deal.flip_profit)}</td>
                            </tr>
                            <tr>
                              <td className="text-muted fw-bold">ROI:</td>
                              <td className="fw-bold text-success">{dealService.formatROI(deal.flip_roi)}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  </Col>

                  {mlsData && (
                    <Col md={6}>
                      <h5>MLS Listing Information</h5>
                      <Card className="bg-light">
                        <Card.Body>
                          <h6>Public Remarks</h6>
                          <p className="mb-3">{mlsData.public_remarks || 'No remarks available'}</p>
                          
                          <Row>
                            <Col xs={6}>
                              <small className="text-muted">Listing Agent</small>
                              <p className="mb-2">{mlsData.listing_agent_full_name || 'N/A'}</p>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted">Listing Office</small>
                              <p className="mb-2">{mlsData.listing_office_name || 'N/A'}</p>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted">Status</small>
                              <p className="mb-2">{mlsData.standard_status || 'N/A'}</p>
                            </Col>
                            <Col xs={6}>
                              <small className="text-muted">Subdivision</small>
                              <p className="mb-2">{mlsData.subdivision_name || 'N/A'}</p>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>

                      <h5 className="mt-4">Notes & Actions</h5>
                      <Form.Group className="mb-3">
                        <Form.Control
                          as="textarea"
                          rows={4}
                          placeholder="Add notes about this deal..."
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </Form.Group>
                      <Button 
                        variant="primary"
                        onClick={handleStatusUpdate}
                        disabled={statusUpdating}
                      >
                        {statusUpdating ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Updating...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                    </Col>
                  )}
                </Row>
              </Tab.Pane>

              {/* Comps Analysis Tab */}
              <Tab.Pane eventKey="comps">
                <DealComps 
                  deal={deal}
                  compDetails={compDetails}
                />
              </Tab.Pane>

              {/* Financials Tab */}
              <Tab.Pane eventKey="financials">
                <DealFinancials deal={deal} />
              </Tab.Pane>

              {/* Map View Tab */}
              <Tab.Pane eventKey="map">
                <DealMap 
                  deal={deal}
                  compDetails={compDetails}
                  isVisible={activeTab === 'map'}
                />
              </Tab.Pane>

              {/* Photos Tab */}
              {mlsData?.photos && mlsData.photos.length > 0 && (
                <Tab.Pane eventKey="photos">
                  <Row className="g-3">
                    {mlsData.photos.map((photo, index) => (
                      <Col key={index} xs={12} md={6} lg={4}>
                        <Card>
                          <Card.Img 
                            variant="top" 
                            src={photo} 
                            alt={`Property photo ${index + 1}`}
                          />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Tab.Pane>
              )}

              {/* Activity Tab */}
              <Tab.Pane eventKey="activity">
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-marker bg-primary"></div>
                    <div className="timeline-content">
                      <h6>{new Date(deal.scan_date).toLocaleDateString()}</h6>
                      <p>Deal found by scanner (Grade: {deal.flip_grade})</p>
                    </div>
                  </div>
                  <div className="timeline-item">
                    <div className="timeline-marker bg-info"></div>
                    <div className="timeline-content">
                      <h6>{new Date(deal.scan_date).toLocaleDateString()}</h6>
                      <p>System calculated ARV at {dealService.formatCurrency(deal.renovated_arv)} based on {deal.total_comps} comps</p>
                    </div>
                  </div>
                  {deal.updated_at && (
                    <div className="timeline-item">
                      <div className="timeline-marker bg-secondary"></div>
                      <div className="timeline-content">
                        <h6>{new Date(deal.updated_at).toLocaleDateString()}</h6>
                        <p>Deal status updated to {deal.deal_status}</p>
                      </div>
                    </div>
                  )}
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
      
      {/* Send to Client Modal */}
      {deal && (
        <SendToClientModal
          show={showSendModal}
          onHide={() => setShowSendModal(false)}
          dealId={deal.id}
          propertyAddress={deal.unparsed_address || deal.address || `${deal.city}, AZ ${deal.postal_code}`}
          dealData={{
            listPrice: deal.list_price,
            roi: deal.flip_roi,
            arv: deal.renovated_arv,
            rehabCost: deal.reno_estimate,
            profit: deal.flip_profit,
            beds: deal.bedrooms,
            baths: deal.bathrooms,
            sqft: deal.living_area,
            yearBuilt: deal.year_built
          }}
        />
      )}
    </Container>
  );
};

export default DealAnalysis;